import { Fragment, useSyncExternalStore } from 'react';
import { api } from '../api.ts';
import { typeOfMake } from './boxTypes.ts';

const known = new Map<string, string | null>();
/** Job numbers whose order is job work given to Opal (092). */
const jobWork = new Set<string>();
/** Job numbers whose board the customer supplies (093). */
const customerMaterial = new Set<string>();
const asked = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;
let queued: string[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

const emit = () => { version += 1; listeners.forEach((l) => l()); };

async function flush() {
  timer = null;
  const batch = queued;
  queued = [];
  if (batch.length === 0) return;
  try {
    const res = await api.get<{
      makes: Record<string, string>; jobWork?: Record<string, boolean>; customerMaterial?: Record<string, boolean>;
    }>(
      `/api/reference/job-makes?numbers=${encodeURIComponent(batch.join(','))}`);
    for (const n of batch) {
      known.set(n, res.makes[n] ?? null);
      if (res.jobWork?.[n]) jobWork.add(n);
      if (res.customerMaterial?.[n]) customerMaterial.add(n);
    }
  } catch {
    for (const n of batch) asked.delete(n);
  }
  emit();
}

function want(number: string) {
  if (asked.has(number)) return;
  asked.add(number);
  queued.push(number);
  if (!timer) timer = setTimeout(() => { void flush(); }, 30);
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export function resetJobMakes() {
  known.clear();
  jobWork.clear();
  customerMaterial.clear();
  asked.clear();
  emit();
}

/**
 * What a job is, in one quiet line beside its number: how it is made (Master,
 * Mono, ...), "Job work" when the order is job work given to Opal, and
 * "Outside" when a stage is planned to go to a job worker. Plain small text
 * joined by dots, not a row of pills, so a job with all three still reads as
 * one line. One component, so every screen that shows the Make shows these too.
 */
export function MakeTag({ number, outside }: { number: string | null | undefined; outside?: boolean }) {
  useSyncExternalStore(subscribe, () => version);
  if (!number) return null;
  want(number);
  const make = known.get(number);
  const parts: { text: string; title: string; out?: boolean }[] = [];
  if (make) { const t = typeOfMake(make); parts.push({ text: t.short, title: t.label }); }
  if (jobWork.has(number)) parts.push({ text: 'Job work', title: 'Job work given to us by the customer' });
  if (customerMaterial.has(number)) parts.push({ text: 'Party\'s board', title: 'The party (customer) supplies the board' });
  if (outside) parts.push({ text: 'Outside', title: 'A stage is planned to go to a job worker', out: true });
  if (parts.length === 0) return null;
  return (
    <span className="job-tags">
      {parts.map((p, i) => (
        <Fragment key={p.text}>
          {i > 0 && ' '}
          {/* The dot belongs to the part before it, so a wrap never starts a line with one. */}
          <span className={p.out ? 'out' : undefined} title={p.title} style={{ whiteSpace: 'nowrap' }}>
            {p.text}{i < parts.length - 1 ? ' ·' : ''}
          </span>
        </Fragment>
      ))}
    </span>
  );
}
